import { useSuspenseQuery } from '@apollo/client';
import { HomeResourceSectionQuery, QUERY_HOME_RESOURCE_SECTION, Resource } from '@edwin-group/people-graphql';
import { useCallback, useMemo } from 'react';
import { useHome } from './useHome';
import { useSchoolContext } from '@/components/Navbar/components/SchoolContext/hook/useSchoolContext';
import { getAuthentication } from '@/hooks/useAuthentication';

interface CategoryWithResources {
  [categoryName: string]: {
    resources: Resource[];
    icon?: string | null;
    color?: string | null;
  };
}

interface CategoryNode {
  documentId: string;
  icon?: {
    url?: string | null;
  } | null;
  category: string;
  color?: string | null;
  subCategories?: Array<{
    documentId: string;
    category: string;
    color?: string | null;
  } | null> | null;
}

export const useResourceHubSection = () => {
  const { homePage } = useHome();
  const { schoolContext } = useSchoolContext();
  const selectedCategories = useMemo(
    () => (homePage?.homePage?.resourceSection?.categories?.nodes || []) as CategoryNode[],
    [homePage?.homePage?.resourceSection],
  );

  const getCategoryIds = useCallback((category: CategoryNode | undefined): string[] => {
    if (!category) return [];
    const ids = [category.documentId];
    if (category.subCategories?.length) {
      ids.push(
        ...category.subCategories
          .filter((sub): sub is NonNullable<typeof sub> => sub !== null)
          .map((sub) => sub.documentId),
      );
    }
    return ids;
  }, []);

  const category1 = selectedCategories[0];
  const category2 = selectedCategories[1];
  const category3 = selectedCategories[2];
  const category4 = selectedCategories[3];

  const userDocumentId = getAuthentication()?.user?.documentId ?? '';

  const categoryIds1 = useMemo(() => getCategoryIds(category1), [getCategoryIds, category1]);
  const categoryIds2 = useMemo(() => getCategoryIds(category2), [getCategoryIds, category2]);
  const categoryIds3 = useMemo(() => getCategoryIds(category3), [getCategoryIds, category3]);
  const categoryIds4 = useMemo(() => getCategoryIds(category4), [getCategoryIds, category4]);

  const { data: data1 } = useSuspenseQuery<HomeResourceSectionQuery>(QUERY_HOME_RESOURCE_SECTION, {
    variables: {
      categoryDocumentIds: categoryIds1,
      schoolContextDocumentId: schoolContext?.documentId ?? '',
      userDocumentId,
    },
    skip: categoryIds1.length === 0,
    fetchPolicy: 'cache-first',
  });

  const { data: data2 } = useSuspenseQuery<HomeResourceSectionQuery>(QUERY_HOME_RESOURCE_SECTION, {
    variables: {
      categoryDocumentIds: categoryIds2,
      schoolContextDocumentId: schoolContext?.documentId ?? '',
      userDocumentId,
    },
    skip: categoryIds2.length === 0,
    fetchPolicy: 'cache-first',
  });

  const { data: data3 } = useSuspenseQuery<HomeResourceSectionQuery>(QUERY_HOME_RESOURCE_SECTION, {
    variables: {
      categoryDocumentIds: categoryIds3,
      schoolContextDocumentId: schoolContext?.documentId ?? '',
      userDocumentId,
    },
    skip: categoryIds3.length === 0,
    fetchPolicy: 'cache-first',
  });

  const { data: data4 } = useSuspenseQuery<HomeResourceSectionQuery>(QUERY_HOME_RESOURCE_SECTION, {
    variables: {
      categoryDocumentIds: categoryIds4,
      schoolContextDocumentId: schoolContext?.documentId ?? '',
      userDocumentId,
    },
    skip: categoryIds4.length === 0,
    fetchPolicy: 'cache-first',
  });

  const categoriesWithResources = useMemo(() => {
    const result: CategoryWithResources = {};

    const parseData = (category: CategoryNode, data: HomeResourceSectionQuery['resources']) => {
      return {
        resources: data?.nodes?.map((node) => node as Resource) || [],
        icon: category.icon?.url,
        color: category.color,
      };
    };

    if (category1 && data1?.resources?.nodes?.length) {
      result[category1.category] = parseData(category1, data1.resources);
    }

    if (category2 && data2?.resources?.nodes?.length) {
      result[category2.category] = parseData(category2, data2.resources);
    }

    if (category3 && data3?.resources?.nodes?.length) {
      result[category3.category] = parseData(category3, data3.resources);
    }

    if (category4 && data4?.resources?.nodes?.length) {
      result[category4.category] = parseData(category4, data4.resources);
    }

    return result;
  }, [category1, category2, category3, category4, data1, data2, data3, data4]);

  return {
    categories: Object.keys(categoriesWithResources),
    categoriesWithResources,
  };
};
